import { BlameLogRefType, BlameLogType, } from '@debbie/common/dist/blamelog/index.js';
import keys from '@debbie/common/dist/cache/keys.js';
import { Site, validateEmail } from '@debbie/common/dist/index.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { UserState } from '@debbie/common/dist/users/index.js';
import { selectableRolesForUpdateUser, } from '@debbie/common/dist/users/roles.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { blameLogs } from '@debbie/cortex/dist/api/index.js';
import * as meAPI from '@debbie/cortex/dist/api/me.js';
import * as usersAPI from '@debbie/cortex/dist/api/users.js';
import { useAllCreditors } from '@debbie/cortex/dist/creditors/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { meEntity, useMe, useRole } from '@debbie/cortex/dist/me/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenantId } from '@debbie/cortex/dist/tenant/index.js';
import { useUserById, usersEntity } from '@debbie/cortex/dist/users/index.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import { applyTargetValue, diff } from '../../utils/index.js';
import Button from '../Button.js';
import Checkbox from '../Checkbox.js';
import { Col, Row } from '../Grid/index.js';
import EditIcon from '../Icons/Edit.js';
import { Modal, PhoneInput } from '../index.js';
import Input from '../Input.js';
import Messages from '../Messages/index.js';
import Panel from '../Panel.js';
import UserType from '../UserType.js';
import CreditorSelector from './CreditorSelector.js';
import TenantSelector from './TenantSelector.js';
const UpdateUserSettings = (props) => {
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const meRole = useRole();
    const meLoadable = useMe();
    const me = h.hasData(meLoadable) ? meLoadable.data : null;
    const tenantId = useTenantId();
    const allCreditorsLoadable = useAllCreditors();
    const allCreditors = h.hasData(allCreditorsLoadable)
        ? allCreditorsLoadable.data
        : null;
    const { user, isMe, messages, errors, setMessages, setErrors } = props;
    // users.role is never updated on role changes; the tenant relation holds the actual role
    const tenantRole = user.tenants.find((t) => t.id === tenantId)?.role ?? user.role;
    const [userRole, setUserRole] = useState(tenantRole);
    const [firstName, setFirstName] = useState(user.firstName);
    const [lastName, setLastName] = useState(user.lastName);
    const [username, setUsername] = useState(user.username);
    const [email, setEmail] = useState(user.email || '');
    const [phone, setPhone] = useState(user.phone || null);
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [creditors, setCreditors] = useState(user.creditors.map((c) => c.id));
    const [tenants, setTenants] = useState(user.tenants.map((t) => t.id));
    const [todoNotifications, setTodoNotifications] = useState(user.emailPreferences.todoNotifications || false);
    const [creditorMessageNotifications, setCreditorMessageNotifications] = useState(user.emailPreferences.creditorMessageNotifications ?? true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [unarchiveModalOpen, setUnarchiveModalOpen] = useState(false);
    const [roleModalOpen, setRoleModalOpen] = useState(false);
    const deleteUserMutation = useMutation(async () => {
        await usersAPI.deleteUserById(user.id);
        q.invalidate(keys.write.modify(usersEntity, user.id));
        c.redirect.redirect('/settings/users');
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'delete', lang.errorEntities.user);
        },
    });
    const archiveUserMutation = useMutation(async () => {
        await usersAPI.archiveUserById(user.id);
        q.invalidate(keys.write.modify(usersEntity, user.id));
        c.redirect.redirect('/settings/users');
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.user);
        },
    });
    const unarchiveUserMutation = useMutation(async () => {
        const res = await usersAPI.unarchiveUserById(user.id);
        q.invalidate(keys.write.modify(usersEntity, user.id));
        setUnarchiveModalOpen(false);
        setMessages([
            res.inviteSent === false
                ? lang.users.userReactivatedInviteFailed
                : lang.users.userReactivated,
        ]);
        setTimeout(() => setMessages([]), 10000);
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.user);
        },
    });
    const resendInviteMutation = useMutation(async () => {
        await usersAPI.resendInviteToUser(user.id);
    }, {
        onSuccess: () => {
            setMessages([lang.users.inviteResent]);
            setTimeout(() => setMessages([]), 10000);
        },
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.user);
        },
    });
    useEffect(() => {
        if (allCreditors != null) {
            setCreditors((creditors) => {
                return creditors
                    .map((c) => (allCreditors ?? []).find((ac) => ac.creditorId === c) ? c : null)
                    .filter((c) => !!c);
            });
        }
    }, [allCreditors]);
    useEffect(() => {
        let newErrors = [...errors];
        if (password !== '' && password.length < 8) {
            if (!errors.find((err) => err === lang.users.passwordTooShort)) {
                newErrors.push(lang.users.passwordTooShort);
            }
        }
        else {
            newErrors = newErrors.filter((err) => err !== lang.users.passwordTooShort);
        }
        if (password !== '' &&
            passwordConfirm !== '' &&
            password !== passwordConfirm) {
            if (!errors.find((err) => err === lang.users.passwordsDontMatch)) {
                newErrors.push(lang.users.passwordsDontMatch);
            }
        }
        else {
            newErrors = newErrors.filter((err) => err !== lang.users.passwordsDontMatch);
        }
        setErrors(newErrors);
    }, [password, passwordConfirm]);
    const updateUser = () => {
        const userUpdate = {
            email: email != '' ? email : undefined,
            phone: phone && phone.number != '' ? phone : null,
            username,
            firstName,
            lastName,
            password: password != '' ? password : undefined,
            emailPreferences: {
                todoNotifications: todoNotifications,
                ...(userRole === 'client' ? { creditorMessageNotifications } : {}),
            },
            role: tenantRole !== userRole ? userRole : undefined,
        };
        const tenantCreditors = user.creditors
            .map((c) => c.id)
            .map((c) => (allCreditors ?? []).find((ac) => ac.creditorId === c) ? c : null)
            .filter((c) => !!c);
        const creditorDiff = diff(tenantCreditors, creditors);
        const removeCreditors = Promise.all(creditorDiff.removed.map((id) => usersAPI.removeCreditor(user.id, id)));
        const addCreditors = Promise.all(creditorDiff.added.map((id) => usersAPI.addCreditor(user.id, { id, scopes: [] })));
        const tenantDiff = diff(user.tenants.map((c) => c.id), tenants);
        const removeTenants = Promise.all(tenantDiff.removed.map((id) => usersAPI.removeTenant(user.id, id)));
        const addTenants = Promise.all(tenantDiff.added.map((id) => usersAPI.addTenant(user.id, { id, scopes: [], role: userRole })));
        const updateUser = isMe
            ? meAPI.update(userUpdate)
            : usersAPI.update(user.id, userUpdate);
        Promise.all([
            updateUser,
            addCreditors,
            removeCreditors,
            addTenants,
            removeTenants,
        ])
            .then(() => {
            setMessages([lang.users.userUpdated]);
            setTimeout(() => {
                setMessages([]);
            }, 10000);
            if (site === Site.Caseworker) {
                q.invalidate(keys.write.modify(usersEntity, user.id));
            }
            if (isMe) {
                q.invalidate(keys.write.create(meEntity));
            }
            for (const tenant of tenants) {
                const blameLog = {
                    details: { userId: user.id },
                    refId: tenant,
                    refType: BlameLogRefType.TenantSettings,
                    type: BlameLogType.UserPasswordUpdate,
                };
                blameLogs.createBlameLog(blameLog, tenant);
            }
        })
            .catch((err) => {
            setErrors([lang.users.updateFailed]);
            setMessages([]);
        });
    };
    const editable = 
    // If you are an admin you can change caseworkers
    (meRole && isAdmin(meRole) && tenantRole === 'caseworker') ||
        // You can always edit yourself
        user.id === me?.id ||
        // Caseworkers and admins can edit clients
        userRole === 'client';
    return (React.createElement(React.Fragment, null,
        React.createElement(Panel, { header: lang.users.userSettings },
            React.createElement(Row, null, props.user.details.meetingAccountType === 'meeting_only' &&
                editable ? null : (React.createElement(UserType, { role: userRole, style: {
                    cursor: !isMe ? 'pointer' : undefined,
                    float: 'right',
                    marginBottom: '10px',
                }, onClick: () => editable &&
                    !isMe &&
                    meRole &&
                    meRole === 'admin' &&
                    userRole !== 'client' &&
                    setRoleModalOpen(true), "data-tooltip-id": "tooltip-default", "data-tooltip-content": userRole !== 'client'
                    ? lang.users.changeRoleNotAdmin
                    : lang.users.cantChangeClientRole },
                lang.roles[userRole],
                ' ',
                editable &&
                    userRole !== 'client' &&
                    !isMe &&
                    meRole &&
                    isAdmin(meRole) && React.createElement(EditIcon, null)))),
            React.createElement(Row, null,
                errors.length > 0 ? (React.createElement(Col, { width: "12" },
                    React.createElement(Messages, { messages: errors, type: "error" }))) : null,
                messages.length > 0 ? (React.createElement(Col, { width: "12" },
                    React.createElement(Messages, { messages: messages, type: "success" }))) : null),
            React.createElement(Row, null,
                React.createElement(Col, { width: "12" },
                    React.createElement(Input, { placeholder: lang.users.firstName, disabled: !editable, value: firstName, onChange: applyTargetValue(setFirstName) }),
                    React.createElement(Input, { placeholder: lang.users.lastName, disabled: !editable, value: lastName, onChange: applyTargetValue(setLastName) }),
                    React.createElement(Input, { type: "email", placeholder: lang.users.email, disabled: !editable, value: email, onChange: (e) => {
                            setEmail(e.target.value);
                            setUsername(e.target.value);
                        } }),
                    React.createElement(PhoneInput, { phone: phone, disabled: !editable, placeholder: lang.users.phone, onChange: (phone) => {
                            setPhone(phone);
                        }, noSaveOnValid: true }),
                    userRole === 'client' && (React.createElement(React.Fragment, null,
                        React.createElement(Input, { type: "password", placeholder: lang.users.password, value: password, onChange: applyTargetValue(setPassword), autoComplete: "new-password" }),
                        React.createElement(Input, { type: "password", placeholder: lang.users.passwordConfirmation, value: passwordConfirm, onChange: applyTargetValue(setPasswordConfirm), autoComplete: "new-password" }))),
                    userRole === 'client' && site === Site.Caseworker ? (React.createElement(CreditorSelector, { selected: creditors, onSelect: setCreditors })) : null,
                    userRole === 'caseworker' || (userRole === 'admin' && editable) ? (React.createElement(TenantSelector, { selected: tenants, onSelect: setTenants })) : null,
                    props.user.details.meetingAccountType !== 'meeting_only' ? (React.createElement(Checkbox, { placeholder: lang.users.todoNotifications, disabled: !editable, checked: todoNotifications, onChange: (e) => {
                            setTodoNotifications(e.target.checked);
                        } })) : null,
                    userRole === 'client' ? (React.createElement(Checkbox, { placeholder: lang.users.creditorMessageNotifications, disabled: !editable, checked: creditorMessageNotifications, onChange: (e) => {
                            setCreditorMessageNotifications(e.target.checked);
                        } })) : null,
                    editable && (React.createElement(Button, { onClick: updateUser, block: true, disabled: (password !== '' &&
                            (password !== passwordConfirm || password.length < 8)) ||
                            firstName === '' ||
                            lastName === '' ||
                            ((userRole === 'caseworker' || userRole === 'admin') &&
                                tenants.length < 1) ||
                            (email != null && email !== '' && !validateEmail(email)) ||
                            (userRole === 'client' && creditors.length === 0) }, lang.misc.save))))),
        !isMe && meRole && isAdmin(meRole) && (React.createElement(React.Fragment, null,
            user.state === UserState.INVITED && (React.createElement(Button, { size: "small", loading: resendInviteMutation.isLoading, onClick: () => resendInviteMutation.mutate(), style: { marginRight: '8px' } }, lang.users.resendInvite)),
            user.state === UserState.ACTIVE && (React.createElement(Button, { size: "small", onClick: () => setArchiveModalOpen(!archiveModalOpen), style: { marginRight: '8px' } }, lang.users.archiveUser)),
            user.state === UserState.ARCHIVED &&
                user.details.ssoTenantId == null && (React.createElement(Button, { size: "small", onClick: () => setUnarchiveModalOpen(!unarchiveModalOpen), style: { marginRight: '8px' } }, lang.users.reactivateUser)),
            React.createElement(Button, { theme: "dangerous", size: "small", onClick: () => setDeleteModalOpen(!deleteModalOpen) }, lang.users.deleteUser))),
        archiveModalOpen && (React.createElement(Modal, { maxWidth: "400px", header: lang.users.sureYouWantToArchiveUser, onToggle: () => setArchiveModalOpen(!archiveModalOpen) },
            React.createElement("p", null, lang.users.archiveUserDescription),
            React.createElement("p", { style: { color: '#c0392b' } },
                React.createElement("strong", null, lang.users.archiveUserWarning)),
            React.createElement(Button, { loading: archiveUserMutation.isLoading, size: "small", block: true, onClick: () => archiveUserMutation.mutate() }, lang.users.archiveUser))),
        unarchiveModalOpen && (React.createElement(Modal, { maxWidth: "400px", header: lang.users.sureYouWantToReactivateUser, onToggle: () => setUnarchiveModalOpen(!unarchiveModalOpen) },
            React.createElement("p", null, lang.users.reactivateUserDescription),
            React.createElement(Button, { loading: unarchiveUserMutation.isLoading, size: "small", block: true, onClick: () => unarchiveUserMutation.mutate() }, lang.users.reactivateUser))),
        deleteModalOpen && (React.createElement(Modal, { maxWidth: "400px", header: lang.users.sureYouWantToDeleteUser, onToggle: () => setDeleteModalOpen(!deleteModalOpen) },
            React.createElement(Button, { theme: "dangerous", loading: deleteUserMutation.isLoading, size: "small", block: true, onClick: () => deleteUserMutation.mutate() }, lang.users.deleteUserPermanently))),
        roleModalOpen && (React.createElement(Modal, { onToggle: () => setRoleModalOpen(!roleModalOpen), header: lang.users.changeRole, maxWidth: "380px" }, selectableRolesForUpdateUser
            .filter((r) => r !== 'client')
            .map((role) => (React.createElement(RoleSelect, { key: role, onClick: () => {
                setUserRole(role);
                setRoleModalOpen(false);
            } },
            React.createElement(UserType, { role: role }, lang.roles[role]),
            React.createElement("p", null, 
            // @ts-ignore
            lang.rolesDescription[role]))))))));
};
const Wrapper = (props) => {
    const meLoadable = useMe();
    const me = h.hasData(meLoadable) ? meLoadable.data : null;
    const meRole = useRole();
    const userLoadable = useUserById(props.userId);
    const user = h.hasData(userLoadable) ? userLoadable.data : null;
    const [messages, setMessages] = useState([]);
    const [errors, setErrors] = useState([]);
    if (!me) {
        return null;
    }
    const isMe = me.id === props.userId;
    if (!meRole) {
        return null;
    }
    const u = isMe ? { ...me, role: meRole } : user;
    return me && u && (isMe || h.isLoaded(userLoadable)) ? (React.createElement(UpdateUserSettings, { isMe: isMe, messages: messages, setMessages: setMessages, errors: errors, setErrors: setErrors, user: u })) : null;
};
export default Wrapper;
const RoleSelect = styled.div `
  margin: 5px 0;
  cursor: pointer;
  background-color: #e6e6e6;
  border-radius: 2px;
  padding: ${THEME.GRID_SPACING}px;

  &:hover {
    opacity: 0.8;
  }
`;
//# sourceMappingURL=UpdateUserSettings.js.map